import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiBookOpen, FiTarget } = FiIcons;

const AddCLOModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    courseCode: '',
    cloNumber: '',
    description: ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.courseCode.trim()) {
      newErrors.courseCode = 'Course code is required';
    } else if (!/^[A-Z]{2,4}\d{3,4}$/.test(formData.courseCode.trim())) {
      newErrors.courseCode = 'Course code format should be like CS101, MATH2001, etc.';
    }

    if (!formData.cloNumber.trim()) {
      newErrors.cloNumber = 'CLO number is required';
    } else if (!/^CLO\d{1,2}$/.test(formData.cloNumber.trim().toUpperCase())) {
      newErrors.cloNumber = 'CLO number format should be like CLO1, CLO2, etc.';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'CLO description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'CLO description should be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...formData,
        courseCode: formData.courseCode.toUpperCase().trim(),
        cloNumber: formData.cloNumber.toUpperCase().trim()
      });
      setFormData({ courseCode: '', cloNumber: '', description: '' });
      setErrors({});
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setFormData({ courseCode: '', cloNumber: '', description: '' });
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiTarget} className="text-primary-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Add Course Learning Outcome</h3>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Create a new Course Learning Outcome for your curriculum
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Course Code */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Course Code *
                </label>
                <div className="relative">
                  <SafeIcon 
                    icon={FiBookOpen} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                  />
                  <input
                    type="text"
                    value={formData.courseCode}
                    onChange={(e) => handleInputChange('courseCode', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      errors.courseCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., CS101, MATH2001, ENG1101"
                  />
                </div>
                {errors.courseCode && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-500 text-sm"
                  >
                    {errors.courseCode}
                  </motion.p>
                )}
                <p className="text-xs text-gray-500">
                  Format: 2-4 letters followed by 3-4 numbers (e.g., CS101, MATH2001)
                </p>
              </div>

              {/* CLO Number */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  CLO Number *
                </label>
                <div className="relative">
                  <SafeIcon 
                    icon={FiTarget} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                  />
                  <input
                    type="text"
                    value={formData.cloNumber}
                    onChange={(e) => handleInputChange('cloNumber', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      errors.cloNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., CLO1, CLO2, CLO3"
                  />
                </div>
                {errors.cloNumber && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-500 text-sm"
                  >
                    {errors.cloNumber}
                  </motion.p>
                )}
                <p className="text-xs text-gray-500">
                  Format: CLO followed by a number (e.g., CLO1, CLO2)
                </p>
              </div>

              {/* CLO Description */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  CLO Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                    errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Apply paraphrasing techniques to rewrite academic texts while maintaining original meaning..."
                />
                {errors.description && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-500 text-sm"
                  >
                    {errors.description}
                  </motion.p>
                )}
                <p className="text-xs text-gray-500">
                  Describe what students should be able to do after completing this learning outcome
                </p>
              </div>

              {/* Example CLO */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Example CLO:</h4>
                <div className="text-sm text-blue-700">
                  <p><strong>Course Code:</strong> ENG101</p>
                  <p><strong>CLO Number:</strong> CLO1</p>
                  <p><strong>Description:</strong> Apply paraphrasing techniques to rewrite academic texts while maintaining the original meaning and proper citation format.</p>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiTarget} />
                  <span>Add CLO</span>
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddCLOModal;